import { TopWinsDateRange } from "../enums/top-wins";
import { DateData } from "../models/date";

export const datePadStart = (num: number): string => {
  return `${num}`.padStart(2, '0');
};

/**
 * @description Returns true only for a valid date that is already in the past.
 * An empty or unparsable date is treated as "not passed" so callers never hide
 * content because of a malformed backend value.
 */
export const isDatePassed = (date?: string | null): boolean => {
  if (!date) return false;
  const time = new Date(date).getTime();
  return !Number.isNaN(time) && time <= Date.now();
};

export const getTopWinsDateRange = (range: TopWinsDateRange): { dateFrom: string; dateTo: string } => {
  const now = new Date();
  const from = new Date(now.getTime());
  from.setHours(0, 0, 0, 0);

  switch (range) {
    case TopWinsDateRange.ThisWeek: {
      const daysSinceMonday = (from.getDay() + 6) % 7;
      from.setDate(from.getDate() - daysSinceMonday);
      break;
    }
    case TopWinsDateRange.ThisMonth: {
      from.setDate(1);
      break;
    }
    // "All time" is capped to the last three months — the backend only keeps top wins
    // for that window, so a wider range would return the same rows anyway.
    case TopWinsDateRange.AllTime: {
      from.setMonth(from.getMonth() - 3);
      break;
    }
  }

  return { dateFrom: from.toISOString(), dateTo: now.toISOString() };
}
export const formatDate = (date: Date): string => {
  const dayOfMonth = datePadStart(date.getDate());
  const month = datePadStart(date.getMonth() + 1);
  const year = date.getFullYear();
  const hours = datePadStart(date.getHours());
  const minutes = datePadStart(date.getMinutes());
  return `${dayOfMonth}/${month}/${year} ${hours}:${minutes}`;
};

export const formatValue = (value?: string | number | null) => (
  value || value === 0 ? value : '-'
);

export const formatFreeDropRemainingTime = (targetDate: Date): string => {
  const now = Date.now();
  const target = new Date(targetDate).getTime();
  const diffMs = target - now;

  // 1. Handle expired dates
  if (diffMs <= 0) return "00m 00s";

  // Convert to seconds
  const diffSeconds = Math.floor(diffMs / 1000);

  // Constants
  const oneMinute = 60;
  const oneHour = 60 * 60;
  const oneDay = 60 * 60 * 24;

  // Case 1: More than 1 day remaining
  if (diffSeconds >= oneDay) {
    const days = Math.floor(diffSeconds / oneDay);
    return `${days} days`;
  }

  // Case 2: Less than 1 day but more than 1 hour
  if (diffSeconds >= oneHour) {
    const hours = Math.floor(diffSeconds / oneHour);
    const minutes = Math.floor((diffSeconds % oneHour) / oneMinute);

    // Pad with leading zero (e.g., "5h 03m")
    const hStr = datePadStart(hours);
    const mStr = datePadStart(minutes);
    return `${hStr}h ${mStr}m`;
  }

  // Case 3: Less than 1 hour (Default)
  const minutes = Math.floor(diffSeconds / oneMinute);
  const seconds = diffSeconds % oneMinute;

  const mStr = datePadStart(minutes);
  const sStr = datePadStart(seconds);
  return `${mStr}m ${sStr}s`;
};

/**
 * @description free drop widget timer: "M:SS" under an hour, above it
 * "Nh" / "Nd" when compact and "H:MM:SS" / "Nd HHh" otherwise
 */
export const formatFreeDropWidgetTime = (remainingMs: number, compact = false): string => {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days) return compact ? `${days}d` : `${days}d ${datePadStart(hours)}h`;
  if (hours) return compact ? `${hours}h` : `${hours}:${datePadStart(minutes)}:${datePadStart(seconds)}`;
  return `${minutes}:${datePadStart(seconds)}`;
};

export const getDateInfo = (date: Date): DateData => {
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hour = date.getHours();
  const minute = date.getMinutes();
  return { month, day, hour, minute };
};

export const formatDateFreeDrop = (date: Date): string => {
  const dayOfMonth = datePadStart(date.getDate());
  const month = datePadStart(date.getMonth() + 1);
  const year = date.getFullYear();
  return `${dayOfMonth}/${month}/${year}`;
};
